import React, {ReactElement, useState, useEffect} from 'react';
import {validateWithHookForm} from '@shared/modules/public/resetpassword/ResetPassword';
import {
  resetPasswordService,
  verifyUserService,
  createPasswordService,
  verifyResetToken
} from '@shared/services/PublicService';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  makeStyles,
  createStyles,
  Theme
  // OutlinedInput
} from '@material-ui/core';
import Loader from '@components/framework/loader/Loader';
import './ResetPassword.scss';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import NavRoutes from 'src/constants/route/NavRoutes';
import {
  FONT_SECONDARY_COLOR,
  BORDER_PRIMARY_COLOR,
  APP
} from 'src/style/variable';
import ButtonLoader from '../../../components/system/ButtonLoader/ButtonLoader';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';

// For Show or Hide Button
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import InputAdornment from '@material-ui/core/InputAdornment';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import LogoIcon from '../login/LogoIcon';
// For Show or Hide Button

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    FormLabel: {
      left: '-0.8rem',
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    resetPasswordBox: {
      display: 'flex',
      justifyContent: 'center',
      width: '50%',
      flexDirection: 'column',
      '@media (min-width: 0px) and (max-width: 1023px)': {
        width: '100%'
      }
    },
    appBar: {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    formWrapper: {
      // borderRadius: 0,
      marginTop: '1rem !important',
      '& .MuiOutlinedInput-notchedOutline ': {
        borderColor: BORDER_PRIMARY_COLOR,
        top: 0,
        '& legend': {
          display: 'none'
        }
      }
    },
    button: {
      fontFamily: APP.FONT_FAMILY.BOLD
    },
    resetTitle: {
      display: 'flex',
      left: 0,
      paddingBottom: '2.5rem',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontSize: '1.7rem'
    },
    subTitle: {
      display: 'flex',
      left: 0,
      paddingBottom: '1.5rem',
      fontFamily: APP.FONT_FAMILY.REGULAR,
      fontSize: '1.2rem'
    },
    resetButton: {
      justifyContent: 'center',
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center'
    },
    copyRightText: {
      fontSize: '1rem'
    },
    logo: {
      '@media (min-width: 0px) and (max-width: 1023px) and (min-height: 500px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '40px'
      },
      '@media (min-height: 0px) and (max-height: 499px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '0px'
      },
      '@media (min-width: 1024px)': {
        display: 'none'
      }
    }
  })
);

const ResetPassword = ({history, match}: any): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslation();

  const {enqueueSnackbar} = useSnackbar();

  const [verifyAccount, setVerifyAccount]: any = useState('');
  const [email, setEmail] = useState<string>('');
  const [buttonsDisable, setButtonsDisable] = useState<boolean>(false);

  const {control, handleSubmit, errors} = useForm({
    mode: 'all',
    resolver: async values => {
      return {values, errors: validateWithHookForm(values)};
    }
  });

  const [formState, setFormState] = useState({
    showPassword: false,
    showConfirmPassword: false,
    loading: false
  });

  const verifyUser = (): void => {
    try {
      const {token} = match.params;
      const data = {
        code: token
      };
      verifyUserService(data)
        .then((res: any) => {
          if (res.data?.email) {
            setEmail(res.data.email);
          }
        })
        .catch(err => {
          enqueueSnackbar(
            err && err.message
              ? err.message
              : t('RESET_PASSWORD.ERROR_OCCURRED'),
            SnackBarConfig('e')
          );
          history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
        });
    } catch (e) {
      enqueueSnackbar(
        e && e.message ? e.message : t('RESET_PASSWORD.ERROR_OCCURRED'),
        SnackBarConfig('e')
      );
      history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
    }
    setFormState({...formState, loading: false});
  };

  const verifyResetCode = (): void => {
    try {
      const {token} = match.params;
      const data = {
        reset_code: token
      };
      verifyResetToken(data)
        .then((res: any) => {
          if (res.data?.email) {
            setEmail(res.data.email);
          }
        })
        .catch(err => {
          enqueueSnackbar(
            err && err.message
              ? err.message
              : t('RESET_PASSWORD.ERROR_OCCURRED'),
            SnackBarConfig('e')
          );
          history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
        });
    } catch (e) {
      enqueueSnackbar(
        e && e.message ? e.message : t('RESET_PASSWORD.ERROR_OCCURRED'),
        SnackBarConfig('e')
      );
      history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
    }
    setFormState({...formState, loading: false});
  };
  useEffect(() => {
    const {path} = match;
    if (path === '/verifyAccount/:token') {
      setFormState({...formState, loading: true});
      setVerifyAccount('verifyAccount');
      verifyUser();
    } else {
      setVerifyAccount('resetPassword');
      // setFormState({ ...formState, loading: false })
      setFormState({...formState, loading: true});
      verifyResetCode();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResetPasswordFormSubmit = async (values: any) => {
    const {token} = match.params;
    setButtonsDisable(true);
    if (verifyAccount === 'verifyAccount') {
      const data = {
        password: values.password,
        confirm_password: values.confirmPassword,
        invitation_code: token
      };
      createPasswordService(data)
        .then(res => {
          enqueueSnackbar(
            res && res.message
              ? res.message
              : t('RESET_PASSWORD.SET_PASSWORD_SUCCESS'),
            SnackBarConfig('s')
          );
          history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
        })
        .catch(err => {
          enqueueSnackbar(
            err && err.message
              ? err.message
              : t('RESET_PASSWORD.SET_PASSWORD_ERROR'),
            SnackBarConfig('e')
          );
        })
        .finally(() => {
          setButtonsDisable(false);
        });
    } else {
      const data = {
        password: values.password,
        confirm: values.confirmPassword,
        reset_code: token
      };
      resetPasswordService(data)
        .then(res => {
          enqueueSnackbar(
            res && res.message
              ? res.message
              : t('RESET_PASSWORD.PASSWORD_RESET_SUCCESS'),
            SnackBarConfig('s')
          );
          history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
        })
        .catch(err => {
          enqueueSnackbar(
            err && err.message
              ? err.message
              : t('RESET_PASSWORD.PASSWORD_RESET_ERROR'),
            SnackBarConfig('e')
          );
        })
        .finally(() => {
          setButtonsDisable(false);
        });
    }
  };

  const renderLogo = (): ReactElement => {
    return (
      <div className={classes.logo}>
        <MedopsLogo style={{height: '100%', width: '30%'}} />
      </div>
    );
  };

  // For Show or Hide Pass
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [
    isConfirmPasswordVisible,
    setIsConfirmPasswordVisible
  ] = useState<boolean>(false);

  const handleClickShowPassword = () => {
    if (isPasswordVisible == true) {
      setIsPasswordVisible(false);
    } else {
      setIsPasswordVisible(true);
    }
  };

  const handleClickShowConfirmPassword = () => {
    if (isConfirmPasswordVisible == true) {
      setIsConfirmPasswordVisible(false);
    } else {
      setIsConfirmPasswordVisible(true);
    }
  };

  // For Show or Hide Pass

  return (
    <div className="resetpassword-wrapper">
      {!formState.loading && (
        <div className="resetpassword-content-wrap">
          <LogoIcon />
          <div className={classes.resetPasswordBox}>
            {renderLogo()}
            <div className="card-wrapper">
              <div className="card-content">
                <div className="resetpassword-form">
                  <Typography className={classes.resetTitle} variant={'h3'}>
                    {verifyAccount === 'verifyAccount'
                      ? t('RESET_PASSWORD.VERIFY_ACCOUNT')
                      : t('RESET_PASSWORD.RESET_PASSWORD')}
                  </Typography>
                  <Typography className={classes.subTitle}>
                    {t('RESET_PASSWORD.SUBTITLE')}
                  </Typography>
                  <form onSubmit={handleSubmit(handleResetPasswordFormSubmit)}>
                    <div>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink className={classes.FormLabel}>
                          {t('RESET_PASSWORD.EMAIL')}
                        </InputLabel>
                        <StyledOutlinedInput
                          className={classes.formWrapper}
                          id="outlined-adornment-email"
                          margin={'dense'}
                          label={t('RESET_PASSWORD.EMAIL')}
                          name={'email'}
                          type={'email'}
                          disabled={true}
                          value={email}
                        />
                      </FormControl>
                    </div>
                    <div>
                      <FormControl
                        error={!!errors.password}
                        fullWidth
                        variant="outlined">
                        <InputLabel shrink className={classes.FormLabel}>
                          {t('RESET_PASSWORD.NEW_PASSWORD')}
                        </InputLabel>
                        <Controller
                          className={classes.formWrapper}
                          id="outlined-adornment-reset-password"
                          as={StyledOutlinedInput}
                          margin={'dense'}
                          label={t('RESET_PASSWORD.NEW_PASSWORD')}
                          name={'password'}
                          control={control}
                          placeholder={t('RESET_PASSWORD.NEW_PASSWORD')}
                          type={isPasswordVisible ? 'text' : 'password'}
                          autoComplete="on"
                          defaultValue=""
                          error={!!errors.password}
                          // For Show or Hide Pass
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword}>
                                {isPasswordVisible ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          // For Show or Hide Pass
                        />
                        {errors.password && (
                          <FormHelperText>{t(errors.password)}</FormHelperText>
                        )}
                      </FormControl>
                    </div>
                    <div style={{marginTop: '1rem'}}>
                      <FormControl
                        error={!!errors.confirmPassword}
                        fullWidth
                        variant="outlined">
                        <InputLabel shrink className={classes.FormLabel}>
                          {t('RESET_PASSWORD.CONFIRM_PASSWORD')}
                        </InputLabel>
                        <Controller
                          className={classes.formWrapper}
                          id="outlined-adornment-reset-confirm-password"
                          as={StyledOutlinedInput}
                          margin={'dense'}
                          label={t('RESET_PASSWORD.CONFIRM_PASSWORD')}
                          name={'confirmPassword'}
                          control={control}
                          placeholder={t('RESET_PASSWORD.CONFIRM_PASSWORD')}
                          type={isConfirmPasswordVisible ? 'text' : 'password'}
                          autoComplete="on"
                          defaultValue=""
                          error={!!errors.confirmPassword}
                          // For Show or Hide Pass
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowConfirmPassword}>
                                {isConfirmPasswordVisible ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          // For Show or Hide Pass
                        />
                        {errors.confirmPassword && (
                          <FormHelperText>
                            {t(errors.confirmPassword)}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </div>
                    <div className={classes.resetButton}>
                      <Button
                        variant="contained"
                        size={'medium'}
                        color="primary"
                        // className={['block', classes.button].join(' ')}
                        type={'submit'}
                        disabled={buttonsDisable}
                        onClick={handleSubmit(handleResetPasswordFormSubmit)}>
                        {buttonsDisable && <ButtonLoader />}
                        {!buttonsDisable &&
                          (verifyAccount === 'verifyAccount'
                            ? t('RESET_PASSWORD.SET_PASSWORD')
                            : t('RESET_PASSWORD.RESET_PASSWORD'))}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {formState.loading && (
        <Loader color="secondary" className={'flex-centering mt-1em mb-1em'} />
      )}
    </div>
  );
};
export default ResetPassword;
